import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import styled from 'styled-components';
import useIndex from '../../../../hooks/useIndex';
import validErrors from '../../../../hooks/validErrors';
import moneyFormat from '../../../../hooks/moneyFormat';
import {
    createCombo,
    deleteCombo,
    getCombos,
    updateCombo,
} from '../../../../data/api/combos/combos';

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        background: '#fff',
        color: '#10285f',
        fontWeight: 700,
        '& fieldset': {
            borderColor: 'rgba(15, 34, 77, 0.16)',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(214, 170, 58, 0.7)',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#d6aa3a',
            borderWidth: '2px',
        },
    },
    '& .MuiInputLabel-root': {
        color: 'rgba(15, 34, 77, 0.65)',
        fontWeight: 700,
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: '#10285f',
    },
};

const Combos = () => {
    const { token } = useIndex();

    const [loading, setLoading] = useState(true);
    const [combos, setCombos] = useState([]);

    const [search] = useState('');
    const [status] = useState('');

    const [open, setOpen] = useState(false);
    const [submit, setSubmit] = useState(false);

    const [editing, setEditing] = useState(false);
    const [editingUuid, setEditingUuid] = useState(null);

    const [form, setForm] = useState({
        name: '',
        value: '',
        moves: '',
        status: 1,
    });

    const canSubmit = useMemo(() => {
        const nameOk = String(form.name || '').trim().length >= 2;
        const valueOk = Number(form.value) > 0;
        const movesOk = Number(form.moves) > 0;

        return nameOk && valueOk && movesOk;
    }, [form]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);

        try {
            const params = {
                ...(search.trim() ? { search: search.trim() } : {}),
                ...(status !== '' ? { status } : {}),
            };

            const { data } = await getCombos(token, params);

            if (data?.success) {
                setCombos(data?.data ?? []);
            } else {
                setCombos([]);
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setForm({
            name: '',
            value: '',
            moves: '',
            status: 1,
        });
        setEditing(false);
        setEditingUuid(null);
    };

    const openCreate = () => {
        resetForm();
        setOpen(true);
    };

    const openEdit = (row) => {
        setEditing(true);
        setEditingUuid(row?.uuid);
        setForm({
            name: row?.name ?? '',
            value: row?.value ?? '',
            moves: row?.moves ?? '',
            status: row?.status ?? 1,
        });
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
        resetForm();
    };

    const handleSave = async () => {
        if (!canSubmit) return;

        setSubmit(true);

        try {
            const payload = {
                name: String(form.name).trim(),
                value: Number(form.value),
                moves: Number(form.moves),
                status: Number(form.status),
            };

            const res = editing && editingUuid
                ? await updateCombo(token, editingUuid, payload)
                : await createCombo(token, payload);

            if (res?.data?.success) {
                onClose();
                loadData();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmit(false);
        }
    };

    const handleDelete = async (row) => {
        const ok = window.confirm(`¿Eliminar el combo "${row?.name}"?`);
        if (!ok) return;

        try {
            await deleteCombo(token, row?.uuid);
            loadData();
        } catch (error) {
            validErrors(error);
        }
    };

    return (
        <PageContainer maxWidth={false}>
            <Header>
                <Box className="icon">
                    <LocalOfferOutlinedIcon />
                </Box>

                <Box>
                    <Typography className="title">
                        Combos
                    </Typography>

                    <Typography className="subtitle">
                        Administra paquetes de jugadas, valores y estados disponibles.
                    </Typography>
                </Box>
            </Header>

            <MainCard>
                <Toolbar>
                    <Box>
                        <Typography className="section-title">
                            Combos configurados
                        </Typography>

                        <Typography className="section-subtitle">
                            Total de registros: <strong>{combos?.length ?? 0}</strong>
                        </Typography>
                    </Box>

                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={openCreate}
                        sx={primaryButtonStyles}
                    >
                        Crear combo
                    </Button>
                </Toolbar>

                {loading ? (
                    <LoadingBox>
                        <CircularProgress sx={{ color: '#d6aa3a' }} />
                        <Typography>Cargando combos...</Typography>
                    </LoadingBox>
                ) : (
                    <StyledTableContainer>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <StyledHeadCell>ID</StyledHeadCell>
                                    <StyledHeadCell>Nombre</StyledHeadCell>
                                    <StyledHeadCell align="right">Valor</StyledHeadCell>
                                    <StyledHeadCell align="right">Jugadas</StyledHeadCell>
                                    <StyledHeadCell align="center">Estado</StyledHeadCell>
                                    <StyledHeadCell align="right">Acciones</StyledHeadCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {combos.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <EmptyState>
                                                <LocalOfferOutlinedIcon />
                                                <Typography className="empty-title">
                                                    No hay combos
                                                </Typography>
                                                <Typography className="empty-text">
                                                    Crea el primer combo para comenzar.
                                                </Typography>
                                            </EmptyState>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    combos.map((row) => (
                                        <StyledTableRow key={row?.uuid ?? row?.id} hover>
                                            <StyledBodyCell>
                                                <Chip
                                                    size="small"
                                                    label={`#${row?.id}`}
                                                    sx={{
                                                        fontWeight: 900,
                                                        color: '#10285f',
                                                        background: 'rgba(16, 40, 95, 0.08)',
                                                    }}
                                                />
                                            </StyledBodyCell>

                                            <StyledBodyCell>
                                                <Box className="name-cell">
                                                    <LocalOfferOutlinedIcon />
                                                    <strong>{row?.name}</strong>
                                                </Box>
                                            </StyledBodyCell>

                                            <StyledBodyCell align="right">
                                                <Box className="money-cell">
                                                    <PaymentsOutlinedIcon />
                                                    {moneyFormat(row?.value ?? 0)}
                                                </Box>
                                            </StyledBodyCell>

                                            <StyledBodyCell align="right">
                                                <Box className="moves-cell">
                                                    <ConfirmationNumberOutlinedIcon />
                                                    {row?.moves ?? 0}
                                                </Box>
                                            </StyledBodyCell>

                                            <StyledBodyCell align="center">
                                                {Number(row?.status) === 1 ? (
                                                    <Chip
                                                        size="small"
                                                        label="Activo"
                                                        sx={{
                                                            fontWeight: 900,
                                                            color: '#0f5c36',
                                                            background: 'rgba(46, 125, 50, 0.12)',
                                                            border: '1px solid rgba(46, 125, 50, 0.25)',
                                                        }}
                                                    />
                                                ) : (
                                                    <Chip
                                                        size="small"
                                                        label="Inactivo"
                                                        sx={{
                                                            fontWeight: 900,
                                                            color: '#9b1c1c',
                                                            background: 'rgba(211, 47, 47, 0.10)',
                                                            border: '1px solid rgba(211, 47, 47, 0.25)',
                                                        }}
                                                    />
                                                )}
                                            </StyledBodyCell>

                                            <StyledBodyCell align="right">
                                                <Tooltip title="Editar">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openEdit(row)}
                                                        sx={iconButtonStyles}
                                                    >
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Eliminar">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(row)}
                                                        sx={deleteButtonStyles}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </StyledBodyCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                )}
            </MainCard>

            <Modal open={open} onClose={onClose}>
                <ModalCenter>
                    <ModalCard>
                        <ModalHeader>
                            <Box className="title-wrap">
                                <Box className="modal-icon">
                                    <LocalOfferOutlinedIcon />
                                </Box>

                                <Box>
                                    <Typography className="modal-title">
                                        {editing ? 'Editar combo' : 'Crear combo'}
                                    </Typography>

                                    <Typography className="modal-subtitle">
                                        Define el nombre, valor, jugadas y estado del combo.
                                    </Typography>
                                </Box>
                            </Box>

                            <IconButton
                                onClick={onClose}
                                size="small"
                                sx={{
                                    color: '#10285f',
                                    background: 'rgba(16, 40, 95, 0.06)',
                                    '&:hover': {
                                        background: 'rgba(214, 170, 58, 0.16)',
                                    },
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </ModalHeader>

                        <Stack spacing={2}>
                            <TextField
                                size="small"
                                label="Nombre"
                                placeholder="Ej: Combo 5K"
                                fullWidth
                                value={form.name}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, name: e.target.value }))
                                }
                                inputProps={{ maxLength: 80 }}
                                sx={fieldStyles}
                            />

                            <Box className="form-grid">
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Valor"
                                    placeholder="Ej: 5000"
                                    fullWidth
                                    value={form.value}
                                    onChange={(e) =>
                                        setForm((prev) => ({ ...prev, value: e.target.value }))
                                    }
                                    inputProps={{ min: 0 }}
                                    sx={fieldStyles}
                                />

                                <TextField
                                    size="small"
                                    type="number"
                                    label="Jugadas"
                                    placeholder="Ej: 5"
                                    fullWidth
                                    value={form.moves}
                                    onChange={(e) =>
                                        setForm((prev) => ({ ...prev, moves: e.target.value }))
                                    }
                                    inputProps={{ min: 1 }}
                                    sx={fieldStyles}
                                />
                            </Box>

                            <FormControl size="small" fullWidth sx={fieldStyles}>
                                <InputLabel>Estado</InputLabel>

                                <Select
                                    label="Estado"
                                    value={form.status}
                                    onChange={(e) =>
                                        setForm((prev) => ({ ...prev, status: e.target.value }))
                                    }
                                >
                                    <MenuItem value={1}>Activo</MenuItem>
                                    <MenuItem value={0}>Inactivo</MenuItem>
                                </Select>
                            </FormControl>

                            <Button
                                variant="contained"
                                disabled={!canSubmit || submit}
                                onClick={handleSave}
                                sx={{
                                    ...primaryButtonStyles,
                                    py: 1.15,
                                    '&.Mui-disabled': {
                                        background: 'rgba(15, 34, 77, 0.12)',
                                        color: 'rgba(15, 34, 77, 0.36)',
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                {submit
                                    ? 'Guardando...'
                                    : editing
                                        ? 'Guardar cambios'
                                        : 'Crear combo'}
                            </Button>
                        </Stack>
                    </ModalCard>
                </ModalCenter>
            </Modal>
        </PageContainer>
    );
};

export default Combos;

const primaryButtonStyles = {
    borderRadius: '14px',
    fontWeight: 900,
    textTransform: 'uppercase',
    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
    boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
    color: '#fff',
    '&:hover': {
        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
        boxShadow: '0 8px 18px rgba(15, 34, 77, 0.24)',
    },
};

const iconButtonStyles = {
    color: '#10285f',
    background: 'rgba(16, 40, 95, 0.06)',
    mr: 0.5,
    '&:hover': {
        color: '#8a6500',
        background: 'rgba(214, 170, 58, 0.16)',
    },
};

const deleteButtonStyles = {
    color: '#9b1c1c',
    background: 'rgba(211, 47, 47, 0.08)',
    '&:hover': {
        color: '#d32f2f',
        background: 'rgba(211, 47, 47, 0.14)',
    },
};

const PageContainer = styled(Container)`
    padding: 1.2rem !important;
    background: #f5f7fb;
    min-height: 100%;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 0.85rem;
    margin-bottom: 1rem;

    .icon {
        width: 46px;
        height: 46px;
        border-radius: 15px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.3);
        color: #10285f;
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
            font-size: 1.55rem;
        }
    }

    .title {
        color: #10285f;
        font-size: 1.65rem;
        font-weight: 900;
        line-height: 1.1;
    }

    .subtitle {
        margin-top: 0.18rem;
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.9rem;
        font-weight: 600;
    }
`;

const MainCard = styled.div`
    padding: 1rem;
    border-radius: 22px;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border: 1px solid rgba(214, 170, 58, 0.25);
    box-shadow: 0 12px 28px rgba(15, 34, 77, 0.08);
`;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;

    .section-title {
        color: #10285f;
        font-size: 1.05rem;
        font-weight: 900;
    }

    .section-subtitle {
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.82rem;
        font-weight: 600;

        strong {
            color: #10285f;
            font-weight: 900;
        }
    }
`;

const StyledTableContainer = styled(TableContainer)`
    max-height: 520px;
    border-radius: 18px;
    background: #fff;
    border: 1px solid rgba(15, 34, 77, 0.1);
    box-shadow: 0 8px 18px rgba(15, 34, 77, 0.05);
`;

const StyledHeadCell = styled(TableCell)`
    background: #10285f !important;
    color: #f8f4e8 !important;
    font-weight: 900 !important;
    font-size: 0.82rem !important;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 2px solid #d6aa3a !important;
    white-space: nowrap;
`;

const StyledBodyCell = styled(TableCell)`
    color: #10285f !important;
    font-size: 0.88rem !important;
    font-weight: 700 !important;
    border-bottom: 1px solid rgba(15, 34, 77, 0.08) !important;

    .name-cell,
    .money-cell,
    .moves-cell {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;

        svg {
            font-size: 1rem;
            color: #d6aa3a;
        }

        strong {
            font-weight: 900;
        }
    }
`;

const StyledTableRow = styled(TableRow)`
    transition: 0.2s ease;

    &:hover {
        background: rgba(214, 170, 58, 0.08) !important;
    }
`;

const LoadingBox = styled.div`
    min-height: 260px;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    align-items: center;
    justify-content: center;
    color: #10285f;
    font-weight: 700;
`;

const EmptyState = styled.div`
    min-height: 180px;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #10285f;

    svg {
        font-size: 2.2rem;
        color: #d6aa3a;
    }

    .empty-title {
        font-weight: 900;
        font-size: 1.1rem;
    }

    .empty-text {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.88rem;
    }
`;

const ModalCenter = styled(Box)`
    min-height: 100vh;
    width: 100vw;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
    box-sizing: border-box;
    outline: none;
`;

const ModalCard = styled.div`
    width: min(720px, 95vw);
    padding: 1.2rem;
    border-radius: 22px;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border: 1px solid rgba(214, 170, 58, 0.28);
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.22);

    .form-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.9rem;
    }

    @media (max-width: 600px) {
        padding: 0.95rem;

        .form-grid {
            grid-template-columns: 1fr;
        }
    }
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;

    .title-wrap {
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }

    .modal-icon {
        width: 42px;
        height: 42px;
        border-radius: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #10285f;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
    }

    .modal-title {
        color: #10285f;
        font-size: 1.25rem;
        font-weight: 900;
    }

    .modal-subtitle {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.84rem;
        font-weight: 600;
        margin-top: 0.1rem;
    }
`;